import { CustomTooltip, IconButton } from '@fountain/fountain-ui-components';
import {
  ExclamationOutlineIcon,
  Icon,
  PencilOutlineIcon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import { Grid, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { useSimpleToggle } from 'hooks';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import { ConfirmationModal } from 'components/ConfirmationModal';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { useStyles } from './styles';

export interface ItemRowProps {
  title: string;
  type?: string;
  /**
   * Something about this item needs the admin's attention before it will work —
   * rendered as an amber pill beside the title, phrased as the action to take
   * (AXHE-4446). Icon + text rather than colour alone, so it survives a
   * colour-blind or greyscale read.
   *
   * Not an error: the row is saveable as it stands. Anything that blocks the
   * save belongs in the surrounding form's own error handling, not here.
   */
  warning?: string;
  id: string | number;
  confirmDeleteModalTitle?: string;
  confirmDeleteModalDescription?: string;
  confirmDeleteModalButton?: string;
  icon?: React.ReactNode;
  onDelete: (id: string | number) => void;
  onEdit: (id: string | number) => void;
  // Props for applying styles based on user input
  hoverable?: boolean;
  dragging?: boolean;
}

export const ItemRow: FC<ItemRowProps> = ({
  title,
  type,
  warning,
  id,
  confirmDeleteModalTitle,
  confirmDeleteModalDescription,
  confirmDeleteModalButton,
  onEdit,
  onDelete,
  icon,
  hoverable = false,
  dragging = false,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const {
    showContent: isDeleteMessageModalOpen,
    toggle: toggleIsDeleteMessageModalOpen,
  } = useSimpleToggle(false);

  const onConfirmDelete = () => {
    onDelete(id);
    toggleIsDeleteMessageModalOpen();
  };

  const onClickEdit = () => {
    onEdit(id);
  };

  return (
    <>
      <Grid
        container
        className={classNames(styles.row, {
          hoverable,
          dragging,
        })}
        alignItems="center"
      >
        {icon}
        {type && (
          <Typography variant="body2" className={styles.type}>
            {type}
          </Typography>
        )}
        <Typography variant="h4" className={styles.title}>
          {title}
        </Typography>
        {/* `h5` is 12px in this theme, not a heading size — the scale is
            inverted from the usual (h4 is 14px, subtitle1 is 24px), so `h5`
            is the subordinate step below the title's `h4`. `component="span"`
            because an <h5> element inside a list row is not a heading. */}
        {warning && (
          <span className={styles.warning}>
            <Icon icon={ExclamationOutlineIcon} size="lg" color="inherit" />
            <Typography variant="h5" component="span">
              {warning}
            </Typography>
          </span>
        )}
        <Grid className={styles.actionIcons}>
          <CustomTooltip dense title={intl.formatMessage(globalMessages.edit)}>
            <IconButton
              aria-label={intl.formatMessage(globalMessages.edit)}
              className={styles.editMessageButton}
              onClick={onClickEdit}
            >
              <Icon
                icon={PencilOutlineIcon}
                size="sm"
                color="inherit"
                className={styles.editMessageIcon}
              />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip
            dense
            title={intl.formatMessage(globalMessages.delete)}
          >
            <IconButton
              aria-label={intl.formatMessage(globalMessages.delete)}
              className={styles.deleteMessageButton}
              onClick={toggleIsDeleteMessageModalOpen}
            >
              <Icon
                icon={TrashOutlineIcon}
                size="sm"
                color="inherit"
                className={styles.deleteMessageIcon}
              />
            </IconButton>
          </CustomTooltip>
        </Grid>
      </Grid>
      {isDeleteMessageModalOpen && (
        <ConfirmationModal
          bodyContent={
            confirmDeleteModalDescription ??
            intl.formatMessage(messages.askDelete)
          }
          cancelButtonContent={intl.formatMessage(globalMessages.cancel)}
          confirmButtonContent={
            confirmDeleteModalButton ??
            intl.formatMessage(globalMessages.delete)
          }
          negative
          onCancel={toggleIsDeleteMessageModalOpen}
          onConfirm={onConfirmDelete}
          title={
            confirmDeleteModalTitle ?? intl.formatMessage(globalMessages.delete)
          }
        />
      )}
    </>
  );
};
